import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateActionUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export type UpdateActionUpdateTRPCInput = {
  Id: string;
  ParentActionId: string;
  Title: string;
  Description: string;
  CustomAttributeData?: CustomAttributeData | null;
  OriginalTimestamp: string;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (): UpdateActionUpdateMutation => {
  return {
    update_action_update: {
      __typename: 'action_update_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useUpdateActionUpdateTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.action.updates.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.action.updates.getActionUpdatesByParentActionId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.updates.getActionUpdateById.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    updateActionUpdate: async (
      variables: UpdateActionUpdateTRPCInput
    ): Promise<UpdateActionUpdateMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
